import React, { useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import { Button } from '@rsos/base-components';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { ALIService } from '../services/ali';
import {
  StyledDescription,
  StyledError,
  StyledSuccess,
} from './TextTo911DebugPanel.styles';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface SimulateTt911LocationControlsProps {
  phoneNumber: string;
}

/**
 * Simulates an ALI location (class_of_service TEXT) arriving with no text
 * session yet, via the real /v1/ali endpoint. Use "Simulate Text-to-911
 * Session" above afterward to create the session and confirm auto-transition.
 */
export function SimulateTt911LocationControls({
  phoneNumber,
}: SimulateTt911LocationControlsProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const store = useStore();
  const aliService = useMemo(() => new ALIService(store), [store]);
  const psap = useCurrentPSAP();

  async function handleSimulate() {
    setStatus('sending');
    setErrorMessage('');

    try {
      await aliService.sendSecondaryLocation({
        latitude: 37.7749,
        longitude: -122.4194,
        phoneNumber,
        agencyId: psap.agency_id,
        classOfService: 'TEXT',
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Unknown error occurred',
      );
    }
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <StyledDescription>
        Simulate an ALI location tagged <code>class_of_service: TEXT</code>{' '}
        arriving for this call with <strong>no text session yet</strong>. The
        call should show the "processing, please wait" card instead of Start
        Chat. Use "Simulate Text-to-911 Session" above afterward to create the
        real session and confirm the UI transitions automatically.
      </StyledDescription>
      <Button
        onClick={handleSimulate}
        btnType="secondary"
        disabled={status === 'sending'}
      >
        <strong>
          {status === 'sending'
            ? 'Sending...'
            : 'Simulate TT911 Location (no session)'}
        </strong>
      </Button>
      {status === 'sent' && (
        <StyledSuccess>
          Location sent with no session. The call should show the TT911
          processing card, not Start Chat.
        </StyledSuccess>
      )}
      {status === 'error' && <StyledError>Error: {errorMessage}</StyledError>}
    </div>
  );
}
